import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Check, Prohibit, CalendarCheck, User, Clock, Lock } from "@phosphor-icons/react";

const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-1 block";
const HORIZON_DAYS = 21; // default 3 weeks in advance (overridden by owner's availability_weeks setting)

const PRESETS = {
  any: { label: "Any · 9:30am – Closing", start: "09:30", end: null },
  morning: { label: "Morning · 9:30am – 3:30pm", start: "09:30", end: "15:30" },
  evening: { label: "Closing · 2pm – Closing", start: "14:00", end: null },
  custom: { label: "Custom…" },
  unavailable: { label: "Unavailable" },
};
function to12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h < 12 ? "am" : "pm";
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, "0")}${ap}` : `${hr}${ap}`;
}
const PRESET_NAMES = { any: "Any", morning: "Morning", evening: "Closing" };
function availLabel(available, start, end, preset) {
  if (!available) return "Unavailable";
  const range = `${start ? to12(start) : "Open"} – ${end ? to12(end) : "Closing"}`;
  return PRESET_NAMES[preset] ? `${PRESET_NAMES[preset]} · ${range}` : range;
}
const STATUS_META = {
  active: { label: "Active", bg: "#DCFCE7" },
  submitted: { label: "Pending approval", bg: "#FEF9C3" },
  declined: { label: "Declined", bg: "#FEE2E2" },
};

function ymd(d) { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtDay(d) { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" }); }
function fmtDate(d) { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
// App is pinned to Hawaii time — "today" must be the current date in Pacific/Honolulu
// regardless of the viewer's device timezone, so lock decisions match the backend.
function hawaiiTodayYmd() { return new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Honolulu" }); }
function hawaiiToday() { return new Date(hawaiiTodayYmd() + "T00:00:00"); }

function buildDates(weeks = 3) {
  const today = hawaiiToday();
  const dow = today.getDay(); // 0=Sun..6=Sat
  const monday = addDays(today, dow === 0 ? -6 : 1 - dow); // back to Monday of current week
  const days = Math.max(1, weeks) * 7;
  return [...Array(days)].map((_, i) => ({ date: ymd(addDays(monday, i)), preset: "any", start: "09:00", end: "17:00" }));
}

export default function AvailabilityPanel({ open, onClose }) {
  const { can, user } = useAuth();
  const canApprove = can("availability_approve");
  const canSettings = can("scheduling_settings");
  const isManager = can("schedule_manage");
  const isOwner = user?.role === "owner";
  const [tab, setTab] = useState("mine");
  const [dates, setDates] = useState(buildDates());
  const [note, setNote] = useState("");
  const [maxH, setMaxH] = useState("");
  const [minH, setMinH] = useState("");
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lockDays, setLockDays] = useState(7);
  const [savingLock, setSavingLock] = useState(false);
  const [availWeeks, setAvailWeeks] = useState(3);
  const [savingWeeks, setSavingWeeks] = useState(false);
  const [lockedWeeks, setLockedWeeks] = useState([]);
  const [locksDisabled, setLocksDisabled] = useState(false);
  const [approvedDates, setApprovedDates] = useState(() => new Set());
  const [employees, setEmployees] = useState([]);
  const [editId, setEditId] = useState(null); // null = editing own availability

  const mondayOf = (date) => { const d = new Date(date + "T00:00:00"); const dow = d.getDay(); return ymd(addDays(d, dow === 0 ? -6 : 1 - dow)); };
  const daysUntil = (date) => Math.round((new Date(date + "T00:00:00") - hawaiiToday()) / 86400000);
  const isLocked = (date) => !isManager && !locksDisabled && (daysUntil(date) < lockDays || lockedWeeks.includes(mondayOf(date)) || approvedDates.has(date));
  const isPublishedLocked = (date) => !isManager && !locksDisabled && lockedWeeks.includes(mondayOf(date));

  const loadMine = useCallback(async () => {
    const base = buildDates(availWeeks);
    const { data } = await api.get("/availability" + (editId ? `?employee_id=${editId}` : ""));
    setStatus(data.status);
    setNote(data.note || "");
    setMaxH(data.max_hours_per_week ?? "");
    setMinH(data.min_hours_per_week ?? "");
    const map = {};
    (data.rows || []).forEach((r) => { if (r.date) map[r.date] = r; });
    const approved = new Set();
    (data.rows || []).forEach((r) => { if (r.date && r.approval_status === "active") approved.add(r.date); });
    setApprovedDates(approved);
    setDates(base.map((d) => {
      const r = map[d.date];
      if (!r) return d;
      return { date: d.date, preset: r.preset || (r.available ? "any" : "unavailable"), start: r.start_time || "09:00", end: r.end_time || "17:00" };
    }));
  }, [editId, availWeeks]);

  const loadPending = useCallback(async () => {
    if (!canApprove) return;
    const { data } = await api.get("/availability/pending");
    setPending(data);
  }, [canApprove]);

  useEffect(() => {
    if (open) {
      loadMine(); loadPending();
      api.get("/scheduling/settings").then((r) => { setLockDays(r.data.availability_lock_days ?? 7); setLocksDisabled(!!r.data.availability_locks_disabled); setAvailWeeks(r.data.availability_weeks ?? 3); }).catch(() => {});
      api.get("/availability/locked-weeks").then((r) => setLockedWeeks(r.data.weeks || [])).catch(() => {});
      if (isOwner) api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
    }
  }, [open, loadMine, loadPending, isOwner]);

  const saveLock = async (n) => {
    setSavingLock(true);
    try { const { data } = await api.put("/scheduling/settings", { values: { availability_lock_days: n } }); setLockDays(data.availability_lock_days); toast.success(`Lock set to ${data.availability_lock_days} days before`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingLock(false); }
  };

  const saveWeeks = async (n) => {
    if (isNaN(n) || n < 1 || n > 52) { toast.error("Enter a number of weeks between 1 and 52"); return; }
    setSavingWeeks(true);
    try { const { data } = await api.put("/scheduling/settings", { values: { availability_weeks: n } }); setAvailWeeks(data.availability_weeks); toast.success(`Availability window set to ${data.availability_weeks} week${data.availability_weeks === 1 ? "" : "s"}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingWeeks(false); }
  };

  const clearAvail = async () => {
    const target = editId || user?.id;
    const name = editId ? (employees.find((e) => e.id === editId)?.name || "this employee") : "your";
    if (!window.confirm(`Clear ${editId ? name + "'s" : "your"} availability? They'll need to re-enter and submit it again. This can't be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/availability/reset?employee_id=${target}`);
      toast.success(`Cleared ${data.cleared} entr${data.cleared === 1 ? "y" : "ies"}. ${editId ? name + " can" : "You can"} resubmit now.`);
      loadMine();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!open) return null;

  const setDate = (date, patch) => setDates((ds) => ds.map((d) => (d.date === date ? { ...d, ...patch } : d)));

  const submit = async () => {
    setBusy(true);
    const rows = dates.map((d) => {
      if (d.preset === "unavailable") return { date: d.date, available: false, preset: "unavailable", start_time: null, end_time: null };
      if (d.preset === "custom") return { date: d.date, available: true, preset: "custom", start_time: d.start, end_time: d.end };
      const p = PRESETS[d.preset] || PRESETS.any;
      return { date: d.date, available: true, preset: d.preset, start_time: p.start, end_time: p.end };
    });
    try {
      const payload = {
        rows, note,
        max_hours_per_week: maxH === "" ? null : parseInt(maxH),
        min_hours_per_week: minH === "" ? null : parseInt(minH),
      };
      if (editId) payload.employee_id = editId;
      const { data } = await api.put("/availability", payload);
      const editName = employees.find((e) => e.id === editId)?.name || "employee";
      toast.success(editId ? `Saved ${editName}'s availability` : (data.status === "submitted" ? "Availability submitted for approval" : "Availability saved"));
      setStatus(data.status); loadPending();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const approve = async (eid) => { try { await api.post(`/availability/approve-all?employee_id=${eid}`); toast.success("Availability approved"); loadPending(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const decline = async (eid) => { try { await api.post(`/availability/decline-all?employee_id=${eid}`); toast.success("Availability declined"); loadPending(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  // group dates into weeks of 7 for display
  const weeks = [];
  for (let i = 0; i < dates.length; i += 7) weeks.push(dates.slice(i, i + 7));
  const sm = STATUS_META[status];

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="availability-panel" onClick={(e) => e.stopPropagation()} className="relative bg-background border-l-2 border-black w-full max-w-lg h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#E0E7FF] border-b-2 border-black px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center"><CalendarCheck size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">Availability</h2>
          </div>
          <button data-testid="availability-close-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white"><X size={16} weight="bold" /></button>
        </div>

        {canApprove && (
          <div className="flex gap-2 px-5 pt-4">
            <button data-testid="avail-tab-mine" onClick={() => setTab("mine")} className={`press h-9 px-4 rounded-full text-sm font-bold border-2 border-black ${tab === "mine" ? "bg-black text-white" : "bg-white"}`}>My availability</button>
            <button data-testid="avail-tab-pending" onClick={() => setTab("pending")} className={`press h-9 px-4 rounded-full text-sm font-bold border-2 border-black flex items-center gap-1.5 ${tab === "pending" ? "bg-black text-white" : "bg-white"}`}>
              Pending {pending.length > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] flex items-center justify-center">{pending.length}</span>}
            </button>
          </div>
        )}

        {tab === "mine" ? (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <p className="text-sm text-black/60">{editId ? "Editing this person's availability — locks don't apply to you." : `Set when you can work for the next ${availWeeks} week${availWeeks === 1 ? "" : "s"}.`}</p>
              {sm && <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black" style={{ backgroundColor: sm.bg }}>{sm.label}</span>}
            </div>

            {canSettings && (
              <div className="rounded-xl border-2 border-black bg-[#DCFCE7] p-3 flex items-center gap-2 flex-wrap" data-testid="avail-weeks-box">
                <CalendarCheck size={16} weight="fill" />
                <span className="text-sm font-bold">Availability window</span>
                <input data-testid="avail-weeks-input" type="number" min="1" max="52" defaultValue={availWeeks}
                  key={availWeeks}
                  onBlur={(e) => { const n = parseInt(e.target.value); if (!isNaN(n) && n !== availWeeks) saveWeeks(n); }}
                  disabled={savingWeeks} className="w-16 h-9 px-2 rounded-lg border-2 border-black bg-white text-sm text-center font-bold" />
                <span className="text-sm">week{availWeeks === 1 ? "" : "s"} ahead employees fill in.</span>
              </div>
            )}

            {isOwner && (
              <div className="rounded-xl border-2 border-black bg-white p-3 space-y-2" data-testid="avail-owner-editor">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-black/50">Owner · edit availability for</p>
                <select data-testid="avail-edit-for" value={editId || ""} onChange={(e) => setEditId(e.target.value || null)}
                  className="w-full h-10 px-3 rounded-lg border-2 border-black bg-white text-sm font-bold">
                  <option value="">Me ({user?.name})</option>
                  {employees.filter((e) => e.id !== user?.id).map((e) => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
                {editId && <p className="text-xs text-black/50">As the owner you can set this person's availability directly (applies immediately, no approval needed).</p>}
                <button data-testid="avail-clear-btn" onClick={clearAvail} disabled={busy}
                  className="press w-full h-10 rounded-full bg-[#FEE2E2] text-red-600 text-sm font-bold border-2 border-black flex items-center justify-center gap-2 disabled:opacity-60">
                  <Prohibit size={15} weight="bold" /> Clear {editId ? "this person's" : "my"} availability &amp; resubmit
                </button>
              </div>
            )}

            <div className="rounded-xl border-2 border-black bg-[#E0E7FF] p-3 flex items-center gap-2 flex-wrap">
              <Lock size={16} weight="fill" />
              <span className="text-sm font-bold">Availability locks</span>
              {canSettings ? (
                <input data-testid="avail-lock-days" type="number" min="0" max="30" defaultValue={lockDays} onBlur={(e) => { const n = parseInt(e.target.value); if (!isNaN(n) && n !== lockDays) saveLock(n); }}
                  disabled={savingLock} className="w-16 h-9 px-2 rounded-lg border-2 border-black bg-white text-sm text-center font-bold" />
              ) : <span className="text-sm font-bold">{lockDays}</span>}
              <span className="text-sm">days before each date.{canSettings ? "" : " Locked dates can't be changed."}</span>
            </div>

            {weeks.map((wk, wi) => (
              <div key={wi}>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-black/50 mb-2">Week {wi + 1} · {fmtDate(wk[0].date)} – {fmtDate(wk[wk.length - 1].date)}</p>
                <div className="space-y-2">
                  {wk.map((d) => {
                    const locked = isLocked(d.date);
                    return (
                      <div key={d.date} data-testid={`avail-day-${d.date}`} className={`rounded-xl border-2 border-black p-3 ${locked ? "bg-black/5 opacity-70" : "bg-white"}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-sm w-24 shrink-0"><span className="font-bold">{fmtDay(d.date)}</span> <span className="text-black/50">{fmtDate(d.date)}</span></span>
                          {locked ? (
                            <span className="flex-1 flex items-center gap-1.5 text-sm font-bold text-black/50"><Lock size={13} weight="fill" /> {PRESETS[d.preset]?.label || "Locked"} · {isPublishedLocked(d.date) ? "schedule published" : (approvedDates.has(d.date) ? "approved" : "locked")}</span>
                          ) : (
                            <select data-testid={`avail-preset-${d.date}`} value={d.preset} onChange={(e) => setDate(d.date, { preset: e.target.value })} className="flex-1 h-9 px-2 rounded-lg border-2 border-black bg-white text-sm">
                              {Object.entries(PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                          )}
                        </div>
                        {!locked && d.preset === "custom" && (
                          <div className="flex items-center gap-2 mt-2 pl-24">
                            <input type="time" value={d.start} onChange={(e) => setDate(d.date, { start: e.target.value })} className="h-9 px-2 rounded-lg border-2 border-black bg-white text-sm" />
                            <span className="text-xs">to</span>
                            <input type="time" value={d.end} onChange={(e) => setDate(d.date, { end: e.target.value })} className="h-9 px-2 rounded-lg border-2 border-black bg-white text-sm" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Min hrs / week</label><input data-testid="avail-min" type="number" min="0" className={inputCls} value={minH} onChange={(e) => setMinH(e.target.value)} placeholder="—" /></div>
              <div><label className={labelCls}>Max hrs / week</label><input data-testid="avail-max" type="number" min="0" className={inputCls} value={maxH} onChange={(e) => setMaxH(e.target.value)} placeholder="—" /></div>
            </div>
            <div>
              <label className={labelCls}>Note / request to scheduler</label>
              <textarea data-testid="avail-note" className={inputCls + " h-24 py-2 resize-none"} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Mornings only while in school; prefer weekends" />
            </div>
            <button data-testid="avail-submit" onClick={submit} disabled={busy} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60 flex items-center justify-center gap-2">
              <Check size={18} weight="bold" /> {busy ? "Saving…" : (editId ? "Save availability" : "Submit availability")}
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {pending.length === 0 ? (
              <div className="border-2 border-dashed border-black/30 rounded-xl p-10 text-center">
                <CalendarCheck size={36} className="mx-auto text-black/30" />
                <p className="font-display text-lg font-bold mt-2">No pending requests</p>
              </div>
            ) : pending.map((p) => {
              return (
                <div key={p.employee_id} data-testid={`avail-pending-${p.employee_id}`} className="rounded-xl border-2 border-black bg-white p-4 hard-shadow-sm">
                  <p className="font-bold flex items-center gap-1 mb-2"><User size={14} /> {p.employee_name} <span className="ml-auto text-[10px] font-bold text-black/50">{p.rows.length} day{p.rows.length === 1 ? "" : "s"}</span></p>
                  <div className="space-y-1 mb-2 max-h-72 overflow-y-auto">
                    {p.rows.map((r) => (
                      <div key={r.id} className="flex items-center gap-2 text-xs">
                        <span className="w-28 font-semibold">{r.date ? `${fmtDay(r.date)} ${fmtDate(r.date)}` : `Day ${r.day_of_week}`}</span>
                        {r.available ? <span className="flex items-center gap-1 text-secondary"><Clock size={11} /> {availLabel(true, r.start_time, r.end_time, r.preset)}</span> : <span className="text-red-600">Unavailable</span>}
                      </div>
                    ))}
                  </div>
                  {p.note && <p className="text-xs bg-[#FEF9C3] border-2 border-black rounded-lg p-2 mb-2">📝 {p.note}</p>}
                  <div className="flex gap-2">
                    <button data-testid={`avail-approve-${p.employee_id}`} onClick={() => approve(p.employee_id)} className="press h-9 px-3 rounded-full bg-secondary text-white text-xs font-bold border-2 border-black flex items-center gap-1"><Check size={13} weight="bold" /> Approve</button>
                    <button data-testid={`avail-decline-${p.employee_id}`} onClick={() => decline(p.employee_id)} className="press h-9 px-3 rounded-full bg-white text-xs font-bold border-2 border-black flex items-center gap-1"><Prohibit size={13} weight="bold" /> Decline</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
